import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, FileText, User, Calendar, CheckCircle2, Edit3, RotateCcw, Clock, Share2, Check } from 'lucide-react';
import { collectionService } from '../../services/collectionService';
import { secretKeyService } from '../../services/secretKeyService';
import { CollectionItem } from '../../types';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { CollectionEditModal } from './CollectionEditModal';
import { RollbackModal } from './RollbackModal';
import { useToast } from '../../context/ToastContext';

export const CollectionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [collection, setCollection] = useState<CollectionItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (id) {
      loadCollectionDetail(id);
    }
  }, [id]);

  const loadCollectionDetail = async (collectionId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await collectionService.fetchCollectionById(collectionId);
      if (!data) {
        setError('Bộ sưu tập không tồn tại hoặc đã bị xóa.');
      } else {
        setCollection(data);
        const ownerState = await secretKeyService.isCollectionOwner(data);
        setIsOwner(ownerState);
      }
    } catch (err: any) {
      setError(err.message || 'Không thể tải thông tin bộ sưu tập.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSingle = async (filename: string) => {
    if (!collection) return;
    setDownloadingFile(filename);
    try {
      await collectionService.downloadSingleFile(collection.zipUrlCurrent, filename);
      toast.success(`Đã giải nén và tải file ${filename} về máy!`);
    } catch (err: any) {
      toast.error(err.message || 'Không thể tải file lẻ.');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleDownloadFullZip = async () => {
    if (!collection) return;
    try {
      await collectionService.downloadFullZip(collection.zipUrlCurrent, collection.name);
      toast.success('Đã bắt đầu tải gói zip bộ sưu tập!');
    } catch (err: any) {
      toast.error(err.message || 'Không thể tải file zip.');
    }
  };

  const handleCopyShareLink = async () => {
    if (!collection) return;
    const code = collection.shortCode || collection.id.slice(0, 8);
    const shareUrl = `${window.location.origin}/c/${code}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Đã sao chép đường dẫn chia sẻ bộ sưu tập!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Không thể sao chép đường dẫn.');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1rem', color: '#666' }}>Đang tải thông tin bộ sưu tập...</div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
        <button
          onClick={() => navigate('/collections')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            backgroundColor: '#ffffff',
            border: '1px solid var(--border-light)',
            fontSize: '0.875rem',
            cursor: 'pointer',
            marginBottom: '1rem',
          }}
        >
          <ArrowLeft size={16} /> Quay lại danh sách
        </button>

        <div style={{ backgroundColor: '#fee2e2', color: 'var(--accent-danger)', padding: '1rem', borderRadius: '0.5rem' }}>
          {error || 'Bộ sưu tập không tồn tại.'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', paddingBottom: '2rem' }}>
      {/* Back button */}
      <button
        onClick={() => navigate('/collections')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          backgroundColor: '#ffffff',
          border: '1px solid var(--border-light)',
          color: 'var(--text-on-light)',
          fontWeight: 500,
          fontSize: '0.875rem',
          cursor: 'pointer',
          marginBottom: '1.25rem',
        }}
      >
        <ArrowLeft size={16} />
        <span>Khám Phá</span>
      </button>

      {/* Main Detail Header Card (ADR 0006 compliant) */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-light)',
          padding: '1.25rem',
          marginBottom: '1.5rem',
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            {/* Title & Owner Pill */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)', margin: 0, lineHeight: 1.3 }}>
                {collection.name}
              </h1>

              {isOwner && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.25rem 0.625rem',
                    borderRadius: '9999px',
                    backgroundColor: '#dcfce7',
                    color: '#15803d',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  <CheckCircle2 size={13} />
                  <span>Bạn là chủ sở hữu</span>
                </span>
              )}
            </div>

            {/* Meta details */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <User size={14} style={{ color: '#0284c7' }} />
                <span>Tác giả: <strong style={{ color: 'var(--text-main)' }}>{collection.author || 'User-0000'}</strong></span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Calendar size={14} style={{ color: '#64748b' }} />
                <span>
                  Đăng: {new Date(collection.createdAt).toLocaleDateString('vi-VN')} {new Date(collection.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              {collection.lastUpdatedAt && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#0284c7', fontWeight: 600 }}>
                  <Clock size={14} />
                  <span>
                    Cập nhật: {new Date(collection.lastUpdatedAt).toLocaleDateString('vi-VN')} {new Date(collection.lastUpdatedAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Structured Action Bar for Mobile & Desktop */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
            {/* Primary Action Row: Tải Cả Bộ + Icon Copy Link */}
            <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
              <button
                onClick={handleDownloadFullZip}
                style={{
                  flex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--primary-action)',
                  color: 'var(--text-on-action)',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(128, 198, 249, 0.3)',
                }}
              >
                <Download size={18} />
                <span>Tải Cả Bộ (.zip)</span>
              </button>

              <button
                onClick={handleCopyShareLink}
                title="Sao chép đường dẫn chia sẻ bộ sưu tập"
                aria-label="Sao chép đường dẫn chia sẻ bộ sưu tập"
                style={{
                  width: '44px',
                  height: '44px',
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.5rem',
                  backgroundColor: copied ? '#dcfce7' : '#f0f9ff',
                  border: copied ? '1px solid #86efac' : '1px solid #7dd3fc',
                  color: copied ? '#15803d' : '#0369a1',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                }}
              >
                {copied ? <Check size={18} /> : <Share2 size={18} />}
              </button>
            </div>

            {/* Secondary Row: Owner Action Controls */}
            {isOwner && (
              <div style={{ display: 'flex', gap: '0.375rem', width: '100%' }}>
                <button
                  onClick={() => setShowEditModal(true)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem 0.375rem',
                    borderRadius: '0.5rem',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #7dd3fc',
                    color: '#0369a1',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  <Edit3 size={15} />
                  <span>Sửa</span>
                </button>

                <button
                  onClick={() => {
                    if (!collection.zipUrlPrevious) {
                      toast.info('Bộ sưu tập này chưa từng chỉnh sửa nên chưa có bản sao lưu trước.');
                      return;
                    }
                    setShowRollbackModal(true);
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem 0.375rem',
                    borderRadius: '0.5rem',
                    backgroundColor: collection.zipUrlPrevious ? '#fffbe6' : '#f8fafc',
                    border: collection.zipUrlPrevious ? '1px solid #fde68a' : '1px solid #e2e8f0',
                    color: collection.zipUrlPrevious ? '#92400e' : '#94a3b8',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                  title={collection.zipUrlPrevious ? 'Khôi phục về bản sao lưu trước' : 'Chưa có bản sao lưu cũ'}
                >
                  <RotateCcw size={15} />
                  <span>Khôi Phục</span>
                </button>

                <button
                  onClick={() => setShowDeleteModal(true)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem 0.375rem',
                    borderRadius: '0.5rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: 'var(--accent-danger)',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={15} />
                  <span>Xóa</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Files List */}
      <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-main)' }}>
        Danh Sách File Từ Điển Thành Phần ({collection.files ? collection.files.length : 0})
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {collection.files && collection.files.map((file, index) => (
          <div
            key={index}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-light)',
              padding: '1rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  backgroundColor: 'var(--accent-highlight)',
                  color: 'var(--text-on-light)',
                  display: 'flex',
                }}
              >
                <FileText size={20} />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-on-light)' }}>
                  {file.filename}
                </h4>
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.125rem', display: 'flex', gap: '0.75rem' }}>
                  <span>Loại: <strong>{file.type}</strong></span>
                  <span>Kích thước: {formatBytes(file.size)}</span>
                  <span>Số lượng: <strong>{file.wordCount.toLocaleString()}</strong> từ</span>
                </div>
              </div>
            </div>

            <button
              disabled={downloadingFile === file.filename}
              onClick={() => handleDownloadSingle(file.filename)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                borderRadius: '0.375rem',
                backgroundColor: '#ffffff',
                border: '1px solid #0284c7',
                color: '#0284c7',
                fontSize: '0.8125rem',
                fontWeight: 700,
                cursor: downloadingFile === file.filename ? 'wait' : 'pointer',
              }}
            >
              <Download size={14} />
              <span>{downloadingFile === file.filename ? 'Đang ăn RAM, chờ tí...' : 'Tải Về (.txt)'}</span>
            </button>
          </div>
        ))}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <DeleteConfirmModal
          collectionId={collection.id}
          collectionName={collection.name}
          authorName={collection.author || 'User-0000'}
          secretKeyHash={collection.secretKeyHash}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={() => {
            setShowDeleteModal(false);
            toast.success('Đã xóa bộ sưu tập thành công.');
            navigate('/collections');
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <CollectionEditModal
          collection={collection}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updated) => {
            setShowEditModal(false);
            setCollection(updated);
            if (id) loadCollectionDetail(id);
          }}
        />
      )}

      {/* Rollback Modal */}
      {showRollbackModal && (
        <RollbackModal
          collection={collection}
          onClose={() => setShowRollbackModal(false)}
          onSuccess={() => {
            setShowRollbackModal(false);
            if (id) loadCollectionDetail(id);
          }}
        />
      )}
    </div>
  );
};
